import { focusContext } from '@modules/focus_context';
import { useCombinedRefs } from '@modules/use_combined_ref';
import React, { forwardRef, ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';

export interface VisibaCortexBaseButtonProps {
	tabIndex?: number;
	disabled?: boolean;
	allowPassThrough?: boolean;
	className?: string;
	role?: string;
	type?: 'submit' | 'reset' | 'button';
	onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
	onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseMove?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseDown?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseUp?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseOver?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onKeyUp?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
	onTouchEnd?: (event: React.TouchEvent<HTMLButtonElement>) => void;
	onTouchMove?: (event: React.TouchEvent<HTMLButtonElement>) => void;
	onTouchStart?: (event: React.TouchEvent<HTMLButtonElement>) => void;
	children?: ReactNode;
	autoFocus?: boolean;
}

// The base_button component does not include "cursor: pointer;" due to
// official standards specifying that: "The cursor is a pointer that indicates
// a link." https://www.w3.org/TR/CSS21/ui.html#propdef-cursor
//
// Microsoft and Apple also specifies that the "correct" interaction is to
// reserve the pointing hand to URL links
// https://docs.microsoft.com/en-gb/windows/win32/uxguide/inter-mouse?redirectedfrom=MSDN
// https://developer.apple.com/design/human-interface-guidelines/macos/user-interaction/mouse-and-trackpad/
//
// If you'd want to have buttons with cursors, that's fine, but you'll need to
// extend the base_button component in order to get that interaction
const elements = {
	content: styled.button`
		border: none;
		margin: 0;
		padding: 0;
		width: auto;
		overflow: visible;
		background: transparent;
		color: inherit;
		font: inherit;
		line-height: normal;
		-webkit-font-smoothing: inherit;
		-moz-osx-font-smoothing: inherit;
		-webkit-appearance: none;
	`,
};

/**
 * # cortex:react-ui-std:comp:BaseButton
 */
export const BaseButton = forwardRef<HTMLButtonElement, VisibaCortexBaseButtonProps>(function BaseButton({ ...props }, ref) {
	// Takes out these values to be used as variables here.
	const {
		children,
		className,
		disabled,
		allowPassThrough,
		// Takes out focus events for override.
		onBlur,
		onFocus,
		// Takes out mouse events for override.
		onClick,
		onMouseDown,
		onMouseUp,
		onMouseEnter,
		onMouseOver,
		onMouseLeave,
		// Takes out keyboard events for override.
		onKeyUp,
		onKeyDown,
		// Takes out touch events for override.
		onTouchEnd,
		onTouchMove,
		onTouchStart,
		onMouseMove,
		// defaults.
		tabIndex = 0,
		type = 'button',
		// Spreads anything else.
		...rest
	} = props;

	const buttonRef = useRef<HTMLButtonElement>(null);
	const combinedRef = useCombinedRefs(ref, buttonRef);
	const [isFocused, setIsFocus] = useState(false);

	if (disabled && isFocused) setIsFocus(false);

	// Focus events
	function handleBlur(event: React.FocusEvent<HTMLButtonElement>): void {
		setIsFocus(false);

		focusContext.current = null;

		if (onBlur) onBlur(event);
	}

	function handleFocus(event: React.FocusEvent<HTMLButtonElement>): void {
		setIsFocus(true);

		focusContext.current = buttonRef.current;

		if (onFocus) onFocus(event);
	}

	// Mouse events
	function handleClick(event: React.MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onClick) onClick(event);
	}

	function handleMouseDown(event: React.MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseDown) onMouseDown(event);
	}
	function handleMouseUp(event: React.MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseUp) onMouseUp(event);
	}

	function handleMouseEnter(event: React.MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseEnter) onMouseEnter(event);
	}

	function handleMouseLeave(event: React.MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseLeave) onMouseLeave(event);
	}

	function handleMouseMove(event: React.MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseMove) onMouseMove(event);
	}

	function handleMouseOver(event: React.MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseOver) onMouseOver(event);
	}

	// Keyboard events
	function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
		if (isFocused && event.key === 'Escape' && focusContext.current) {
			focusContext.current.blur();

			return;
		}

		if ((!disabled || allowPassThrough) && onKeyDown) onKeyDown(event);
	}

	function handleKeyUp(event: React.KeyboardEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onKeyUp) onKeyUp(event);
	}

	// Touch events
	function handleTouchStart(event: React.TouchEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onTouchStart) onTouchStart(event);
	}

	function handleTouchEnd(event: React.TouchEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onTouchEnd) onTouchEnd(event);
	}

	function handleTouchMove(event: React.TouchEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onTouchMove) onTouchMove(event);
	}

	return (
		<elements.content
			ref={combinedRef}
			className={className}
			aria-disabled={disabled}
			tabIndex={tabIndex}
			onBlur={handleBlur}
			onClick={handleClick}
			onFocus={handleFocus}
			onKeyDown={handleKeyDown}
			onKeyUp={handleKeyUp}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onMouseUp={handleMouseUp}
			onTouchEnd={handleTouchEnd}
			onTouchMove={handleTouchMove}
			onTouchStart={handleTouchStart}
			onMouseOver={handleMouseOver}
			type={type}
			{...rest}
		>
			{children}
		</elements.content>
	);
});
